
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator'
import { Observer } from 'mobx-vue'
import ContentCommentUI from './Comment.vue'
import type { Options } from '@xt/client/entities/course-v2/user-content'
import { Bind, Debounce } from 'lodash-decorators'
import InfiniteLoading, { StateChanger } from 'vue-infinite-loading'
import EmptyStatus from '@xt/www/components/emptyStatus/index.vue'
import { EnumEmptyStatus } from '@xt/client/utils/empty-status/index'
import { EnumLoadResult } from '@xt/client/utils/infinite-loading'

type TabConfig =
  | {
      type: 'comment'
      label: '评论'
    }
  | {
      type: 'review'
      label: '评阅'
    }

@Observer
@Component({
  components: { ContentCommentUI, EmptyStatus, InfiniteLoading }
})
export default class ContentComments extends Vue {
  @Prop({ required: true }) options: Options
  // 当前的内容主体是否属于自己
  @Prop({ required: true }) selfContent: boolean
  // 当前登录用户的id
  @Prop({ required: true }) selfMemberId: string
  // 当前内容主体的id
  @Prop({ required: true }) contentMemberId: string

  tabs: TabConfig[] = []
  current: number = 0

  get EnumEmptyStatus() {
    return EnumEmptyStatus
  }

  get CommentsStore() {
    return this.$store.$courseV2.UserContent.UserComments
  }

  handleResCallback(res: EnumLoadResult, $state: StateChanger) {
    if (res === EnumLoadResult.Cancel) {
      console.log('其他异常')
    }

    if (res === EnumLoadResult.Error) {
      $state.error()
    }

    if (res === EnumLoadResult.Loaded) {
      $state.loaded()
    }

    if (res === EnumLoadResult.Complete) {
      // 处理第一次加载的数据不够pageSize的情况
      if (this.tabs[this.current].type === 'review' && this.CommentsStore.reviewList.length > 0) {
        $state.loaded()
      } else if (this.tabs[this.current].type === 'comment' && this.CommentsStore.commentList.length > 0) {
        $state.loaded()
      }
      $state.complete()
    }
  }

  async infiniteHandler($state: StateChanger) {
    let res: EnumLoadResult

    if (this.tabs[this.current].type === 'review') {
      res = await this.handleLoadReview()
      this.handleResCallback(res, $state)
    } else if (this.tabs[this.current].type === 'comment') {
      res = await this.handleLoadComment()
      this.handleResCallback(res, $state)
    }
  }

  @Watch('options', { deep: true, immediate: true })
  handleOptionsChange(options: Options) {
    const _tabs: TabConfig[] = []

    if ('homeworkId' in options && this.selfContent) {
      _tabs.push({ type: 'review', label: '评阅' })
    }

    if ('momentId' in options) {
      _tabs.push({ type: 'comment', label: '评论' })
    }

    // 拉取第二tabs数据
    if (_tabs.length > 1) {
      if (_tabs[1].type === 'comment') {
        this.handleLoadComment()
      } else if (_tabs[1].type === 'review') {
        this.handleLoadReview()
      }
    }

    // 初始化通知一下父组件当前的tab
    if (_tabs[0]) {
      this.$emit('switch-tab', _tabs[0].type)
    }

    this.tabs = _tabs
  }

  switchTab(tab: TabConfig, index: number) {
    if (index === this.current) return

    this.current = index

    this.$emit('switch-tab', tab.type)
  }

  async handleLoadReview() {
    if (!('homeworkId' in this.options)) return EnumLoadResult.Cancel

    const res = await this.CommentsStore.loadUserReview(this.options.homeworkId)

    return res
  }

  handleLoadComment() {
    if (!('momentId' in this.options)) return EnumLoadResult.Cancel

    // 假数据状态不继续网络请求
    if (this.$store.$courseV2.GuideData.isGuide) {
      return EnumLoadResult.Cancel
    }

    const res = this.CommentsStore.loadUserComment(this.options.momentId)

    return res
  }

  @Debounce(500, { leading: true, trailing: false })
  @Bind()
  async handleLoadMoreChildComment(rootCommentId: string) {
    if (!('momentId' in this.options)) return

    await this.CommentsStore.loadChildComment(this.options.momentId, rootCommentId)
  }

  handleReplyComment(data: { commentId: string | number; rootCommentId: string | number; toMemberId: string; toMemberNickname: string }) {
    this.$emit('reply-comment', data)
  }

  @Debounce(500, { leading: true, trailing: false })
  @Bind()
  async handleLikeComment(data: { commentId: string; rootCommentId: string }) {
    await this.CommentsStore.likeComment(data.commentId, data.rootCommentId)
  }

  async handleRemoveReview(data: { commentId: number }) {
    const success = await this.CommentsStore.removeItem({ type: 'review', commentId: data.commentId })

    if (success) {
      this.$message.success('删除成功')
    }
  }

  async handleRemoveComment(data: { commentId: string; rootCommentId: string }) {
    const success = await this.CommentsStore.removeItem({ type: 'comment', commentId: data.commentId, rootCommentId: data.rootCommentId })

    if (success) {
      this.$message.success('删除成功')
    }
  }

  created() {}
  mounted() {}
  updated() {}
  destroyed() {
    this.CommentsStore.reset()
  }
}
